// @ts-check
/// <reference path="../types/index.js" />

import { v4 as uuidv4 } from 'uuid';
import { restV2Get } from '../util/RestV2';
import { useLocation } from 'react-router';
import { useContext, useState } from 'react';
import SelectTabContextProvider from '../components/SelectTabContext/SelectTabContext';

function useAuditLogs() {
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingWindow, setLoadingWindow] = useState({});
  const [allAuditLogs, setAllAuditLogs] = useState({});

  /**
   * Using the useContext hook to access context from SelectTabContextProvider.
   * @type {SelectTabContextValue}
   */
  const { auditLogs, setAuditLogs } = useContext(SelectTabContextProvider);

  const fetchAuditLogs = async (windowLabel, currentPage) => {
    setLoadingWindow((prev) => ({ ...prev, [windowLabel]: true }));
    setLoading(true);
    try {
      if (!location?.state?.accountNumber) {
        return;
      }
      const response = await restV2Get(`accounts/${location?.state?.accountNumber}/events`, {
        window: windowLabel
          .replace('Add a Note', 'Account Note')
          .replace('Important Notes', 'Account Note')
          .replace('Account Notes', 'Account Note'),
        page_number: currentPage + 1,
        page_size: 200,
        order: 'desc',
      });
      setLoadingWindow((prev) => ({ ...prev, [windowLabel]: false }));

      if (response.total_records <= 0) {
        setLoading(false);
        return;
      }

      // Find the index of existing logs with the same label
      const existingLogsIndex = auditLogs.findIndex((log) => log.label === windowLabel);
      if (existingLogsIndex !== -1) {
        // If logs with the same label exist, replace them with new logs
        setAuditLogs((prevAuditLogs) => {
          const updatedLogs = [...prevAuditLogs];
          updatedLogs[existingLogsIndex] = {
            ...updatedLogs[existingLogsIndex],
            totalRecords: response.total_records,
            logs: response.records.map((record) => ({ ...record, id: uuidv4() })),
          };
          return updatedLogs;
        });
        setLoading(false);
        return;
      }

      // If logs with the same label do not exist, add new logs
      setAuditLogs((prevAuditLogs) => [
        ...prevAuditLogs,
        {
          active: false,
          label: windowLabel,
          totalRecords: response.total_records,
          logs: response.records.map((record) => ({ ...record, id: uuidv4() })),
        },
      ]);
      setLoading(false);
    } catch (error) {
      setLoadingWindow((prev) => ({ ...prev, [windowLabel]: false }));
      setError(error.message);
      setLoading(false);
    }
  };

  const getAllAuditLogs = async (windows) => {
    try {
      if (!location?.state?.accountNumber) {
        return;
      }
      let response = [];
      response = await restV2Get(`accounts/${location?.state?.accountNumber}/events`, {
        distincton: 'mst_trigger_events.field',
        groupby: 'mst_trigger_events.field',
        schema: 'mst_trigger_events.field,mst_trigger.trigger_desc',
        window: windows,
        page_number: 1,
        page_size: 9999,
        order: 'desc',
      });

      if (response.records <= 0) {
        setLoading(false);
        return;
      }

      setAllAuditLogs(response.records);

      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return { loading, loadingWindow, error, fetchAuditLogs, allAuditLogs, getAllAuditLogs };
}

export default useAuditLogs;
